import React, { useState, useEffect, useMemo } from 'react';
import { AlertTriangle, Search, Eye, CheckCircle, XCircle, Clock, User, MessageSquare } from 'lucide-react';
import { getAllComplaints, resolveComplaint } from '../../../services/adminApi';
import { PageSkeleton } from '../DashboardLoader';

const normalizeComplaint = (complaint = {}) => {
  const createdAt = complaint.created_at || complaint.date || null;
  const createdLabel = createdAt
    ? new Date(createdAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
    : 'N/A';
  const rawStatus = String(complaint.status || 'pending').toLowerCase();
  const status = rawStatus === 'resolved' ? 'Resolved' : rawStatus.includes('progress') ? 'In Progress' : 'Pending';
  const subject = complaint.subject || 'Réclamation';
  const description = complaint.description || complaint.message || '';
  const userLabel = complaint.user || complaint.user_email || (complaint.user_id ? `Utilisateur #${complaint.user_id}` : 'Utilisateur inconnu');

  const match = String(subject).match(/\(#\d+\s*-\s*([^)]+)\)/);
  const restaurant = complaint.restaurant || (match ? match[1] : 'Restaurant non spécifié');

  return {
    ...complaint,
    subject,
    description,
    user: userLabel,
    email: complaint.email || complaint.user_email || '',
    restaurant,
    date: createdLabel,
    status,
    priority: complaint.priority || 'medium',
  };
};

const AdminComplaints = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responseText, setResponseText] = useState('');

  // Load complaints on component mount
  useEffect(() => {
    loadComplaints();
  }, []);

  const loadComplaints = async () => {
    try {
      setLoading(true);
      const data = await getAllComplaints();
      const raw = data?.complaints || data || [];
      const normalized = Array.isArray(raw) ? raw.map(normalizeComplaint) : [];
      setComplaints(normalized);
    } catch (error) {
      console.error('Error loading complaints:', error);
      setError('Erreur lors du chargement des réclamations');
      setComplaints([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredComplaints = complaints.filter(complaint => {
    const user = String(complaint.user || '').toLowerCase();
    const subject = String(complaint.subject || '').toLowerCase();
    const restaurant = String(complaint.restaurant || '').toLowerCase();
    const matchesSearch = user.includes(searchTerm.toLowerCase()) ||
                         subject.includes(searchTerm.toLowerCase()) ||
                         restaurant.includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || complaint.status.toLowerCase() === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  const complaintStats = useMemo(() => {
    const list = Array.isArray(complaints) ? complaints : [];
    const st = (s) => String(s || '').toLowerCase();
    const pr = (p) => String(p || '').toLowerCase();
    return {
      pending: list.filter((c) => st(c.status).includes('pending')).length,
      inProgress: list.filter((c) => st(c.status).includes('progress')).length,
      resolved: list.filter((c) => st(c.status).includes('resolved')).length,
      urgent: list.filter((c) => pr(c.priority) === 'high').length,
    };
  }, [complaints]);

  /** Badges neutres (pas de jaune/bleu/vert/rouge vifs) */
  const badgeNeutral = 'bg-gray-100 text-gray-700 border border-gray-200/80';

  const handleStatusChange = async (complaintId, newStatus) => {
    try {
      if (newStatus === 'Resolved') {
        await resolveComplaint(complaintId);
      }
      // Reload complaints after status change
      await loadComplaints();
    } catch (error) {
      console.error('Error updating complaint status:', error);
      setError('Erreur lors de la mise à jour du statut');
    }
  };

  const handleSendResponse = async () => {
    if (!selectedComplaint || !responseText.trim()) return;
    
    try {
      // Here you would call an API to send the response
      console.log('Sending response:', responseText);
      setResponseText('');
      setSelectedComplaint(null);
      // Reload complaints
      await loadComplaints();
    } catch (error) {
      console.error('Error sending response:', error);
      setError('Erreur lors de l\'envoi de la réponse');
    }
  };

  if (loading) return <PageSkeleton />;

  return (
    <div className="p-4">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Gestion des Réclamations</h1>
        <p className="text-gray-600">Gérez et traitez les réclamations des utilisateurs</p>
        {error && (
          <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-lg">
            {error?.message || error?.toString() || 'Une erreur est survenue'}
          </div>
        )}
      </div>

      {/* Stats Cards — icônes sobres comme le reste de l’admin */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2.5 rounded-full bg-gray-100">
              <Clock className="w-6 h-6 text-gray-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">En attente</p>
              <p className="text-2xl font-bold text-gray-900">{complaintStats.pending}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2.5 rounded-full bg-gray-100">
              <AlertTriangle className="w-6 h-6 text-gray-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">En cours</p>
              <p className="text-2xl font-bold text-gray-900">{complaintStats.inProgress}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2.5 rounded-full bg-gray-100">
              <CheckCircle className="w-6 h-6 text-gray-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Résolues</p>
              <p className="text-2xl font-bold text-gray-900">{complaintStats.resolved}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2.5 rounded-full bg-gray-100">
              <XCircle className="w-6 h-6 text-gray-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Priorité haute</p>
              <p className="text-2xl font-bold text-gray-900">{complaintStats.urgent}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher par utilisateur, sujet ou restaurant..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgb(5, 70, 37)]"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgb(5, 70, 37)]"
            >
              <option value="all">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="in progress">En cours</option>
              <option value="resolved">Résolues</option>
              <option value="closed">Fermées</option>
            </select>
          </div>
        </div>
      </div>

      {/* Complaints List */}
      <div className="bg-white rounded-lg">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">Réclamations ({filteredComplaints.length})</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {filteredComplaints.map((complaint) => (
            <div key={complaint.id} className="p-4 hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-medium text-gray-900">{complaint.subject}</h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${badgeNeutral}`}>
                      {complaint.status}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${badgeNeutral}`}>
                      {complaint.priority}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4 text-gray-500" />
                      {complaint.user}
                    </div>
                    <div className="flex items-center gap-1">
                      <MessageSquare className="w-4 h-4 text-gray-500" />
                      {complaint.restaurant}
                    </div>
                    <span>{complaint.date}</span>
                  </div>
                  <p className="text-gray-700 text-sm line-clamp-2">{complaint.description}</p>
                  {complaint.response && (
                    <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                      <strong>Réponse :</strong> {complaint.response}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    type="button"
                    onClick={() => setSelectedComplaint(complaint)}
                    className="p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
                    title="Voir détails"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  {complaint.status === 'Pending' && (
                    <button
                      type="button"
                      onClick={() => handleStatusChange(complaint.id, 'In Progress')}
                      className="px-3 py-1.5 text-sm rounded-lg bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors"
                    >
                      Traiter
                    </button>
                  )}
                  {complaint.status === 'In Progress' && (
                    <button
                      type="button"
                      onClick={() => handleStatusChange(complaint.id, 'Resolved')}
                      className="px-3 py-1.5 text-sm text-white rounded-lg hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: 'rgb(5, 70, 37)' }}
                    >
                      Résoudre
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Complaint Detail Modal */}
      {selectedComplaint && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Détails de la réclamation</h3>
                <button
                  onClick={() => setSelectedComplaint(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Sujet</h4>
                <p className="text-gray-700">{selectedComplaint.subject}</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                <p className="text-gray-700">{selectedComplaint.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Utilisateur</h4>
                  <p className="text-gray-700">{selectedComplaint.user}</p>
                  <p className="text-sm text-gray-500">{selectedComplaint.email}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Restaurant</h4>
                  <p className="text-gray-700">{selectedComplaint.restaurant}</p>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Réponse</h4>
                <textarea
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgb(5, 70, 37)]"
                  rows={4}
                  placeholder="Tapez votre réponse ici..."
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setSelectedComplaint(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSendResponse}
                className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                style={{ backgroundColor: 'rgb(5, 70, 37)' }}
              >
                Envoyer la réponse
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminComplaints;







